import { useEffect, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import {
  RetailerNexusLocations,
  RetailerNexusLocationsResponse,
} from '../../../../queries/calculate-tax'
import { FEATURE_ENABLED_AUTOTAX_FOR_DELIVERIES } from '../../../../constants/wr-features'
import { settingsStore } from '../../../../factories/database/settings-store'
import { retailerSettingsStore } from '../../../../factories/database/retailer-settings-store'
import { getFeatureFromCache } from '../../../../util/feature-cache'

export const AUTOTAX_SETTINGS_KEY = 'AUTOTAX_SETTINGS'

export interface AutoTaxSettings {
  isDeliveryTaxEnabled: boolean
}

/**
 * Fetches the latest nexus locations from GraphQL and persists to IndexedDB.
 * Called on initial mount (if no cached value) and whenever retailer settings sync (~5 min cycle).
 */
export const refreshAutotaxSettings = async (
  client: ReturnType<typeof useApolloClient>
): Promise<AutoTaxSettings | undefined> => {
  try {
    const result = await client.query<RetailerNexusLocationsResponse>({
      query: RetailerNexusLocations,
      fetchPolicy: 'network-only',
    })
    const settings: AutoTaxSettings = {
      isDeliveryTaxEnabled:
        result.data?.retailerNexusLocations?.isDeliveryTaxEnabled ?? false,
    }
    await settingsStore.put(AUTOTAX_SETTINGS_KEY, settings)
    return settings
  } catch {
    // Non-critical: will retry on next retailer settings sync
    return undefined
  }
}

/**
 * Manages autotax settings (e.g. isDeliveryTaxEnabled) with IndexedDB-backed caching.
 *
 * On mount, hydrates from IndexedDB for an instant cached value. If no cache exists,
 * fetches from GraphQL immediately. Refreshes automatically whenever retailer settings
 * sync (~5 min cycle) and writes through to IndexedDB for future sessions.
 */
export const useAutotaxSettings = (): AutoTaxSettings => {
  const client = useApolloClient()
  const [isDeliveryTaxEnabled, setIsDeliveryTaxEnabled] = useState(false)
  const isAutoTaxFeatureFlagEnabled = getFeatureFromCache(
    FEATURE_ENABLED_AUTOTAX_FOR_DELIVERIES
  )

  useEffect(() => {
    if (!isAutoTaxFeatureFlagEnabled) return

    // Guards against state updates after unmount. Since init() and updateSettings()
    // are async, the component may unmount before they resolve — setting state on an
    // unmounted component is a no-op but indicates a potential memory leak.
    let cancelled = false

    const updateSettings = async () => {
      const settings = await refreshAutotaxSettings(client)
      if (!cancelled && settings?.isDeliveryTaxEnabled !== undefined) {
        setIsDeliveryTaxEnabled(settings.isDeliveryTaxEnabled)
      }
    }

    const init = async () => {
      const cached = await settingsStore.get(AUTOTAX_SETTINGS_KEY)
      if (cancelled) return
      if (cached?.isDeliveryTaxEnabled !== undefined) {
        setIsDeliveryTaxEnabled(cached.isDeliveryTaxEnabled)
      } else {
        await updateSettings()
      }
    }

    init()

    retailerSettingsStore.on('sync', updateSettings)

    return () => {
      cancelled = true
      retailerSettingsStore.off('sync', updateSettings)
    }
  }, [client, isAutoTaxFeatureFlagEnabled])

  return { isDeliveryTaxEnabled }
}
